import { useState, useEffect } from "react";
import { playerService } from "../../services/api";
import toast from "react-hot-toast";

const HighlightsModal = ({
  isOpen,
  onClose,
  highlights,
  onSave,
  editingAsAgent = false,
  playerWyscoutId = null,
}) => {
  const [localHighlights, setLocalHighlights] = useState(highlights || []);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Update local state when highlights prop changes
  useEffect(() => {
    if (isOpen) {
      const safeHighlights = Array.isArray(highlights) ? highlights : [];
      // Map the highlights to match our local format if needed
      const mappedHighlights = safeHighlights.map((h) => ({
        id: h.id,
        title: h.title || "",
        link: h.url || h.link || "",
        order: h.order || 0,
        is_public: h.is_public !== undefined ? h.is_public : true,
      }));
      setLocalHighlights(mappedHighlights);
    }
  }, [isOpen, highlights]);

  // Save highlights to API
  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      if (editingAsAgent && !playerWyscoutId) {
        throw new Error("Player identifier not available for agent updates");
      }

      const apiHighlights = localHighlights.map((h, index) => {
        const link = (h.link || "").trim();
        return {
          ...(h.id > 0 ? { id: h.id } : {}),
          title: h.title || "",
          url: link,
          order:
            typeof h.order === "number" && Number.isFinite(h.order)
              ? h.order
              : index,
          is_public: h.is_public !== undefined ? h.is_public : true,
        };
      });

      const invalidHighlight = apiHighlights.find(
        (highlight) => !highlight.url
      );

      if (invalidHighlight) {
        throw new Error("Please provide a valid link for each highlight");
      }

      const originalIds = Array.isArray(highlights)
        ? highlights.map((h) => h.id).filter((id) => id > 0)
        : [];
      const currentIds = localHighlights
        .map((h) => h.id)
        .filter((id) => id > 0);
      const idsToDelete = originalIds.filter((id) => !currentIds.includes(id));

      if (idsToDelete.length) {
        const deletionResponses = await Promise.all(
          idsToDelete.map((id) =>
            editingAsAgent
              ? playerService.deleteAgentPlayerHighlight(playerWyscoutId, id)
              : playerService.deletePlayerHighlight(id)
          )
        );

        const failedDeletion = deletionResponses.find(
          (result) => !result?.success
        );

        if (failedDeletion) {
          throw new Error(failedDeletion.error || "Failed to delete highlight");
        }
      }

      let savedHighlights = [];

      if (apiHighlights.length) {
        if (editingAsAgent) {
          const payload =
            apiHighlights.length === 1 ? apiHighlights[0] : apiHighlights;
          const response = await playerService.createAgentPlayerHighlight(
            playerWyscoutId,
            payload
          );

          if (response.success) {
            const results = response.data?.results ?? response.data ?? [];
            savedHighlights = Array.isArray(results) ? results : [results];
          } else {
            throw new Error(response.error || "Failed to save highlights");
          }
        } else {
          const response = await playerService.bulkUpsertPlayerHighlights(
            apiHighlights
          );

          if (response.success) {
            savedHighlights = response.data;
          } else {
            throw new Error(response.error || "Failed to save highlights");
          }
        }
      } else {
        savedHighlights = [];
      }

      onSave(savedHighlights);
      toast.success("Highlights saved successfully");
    } catch (err) {
      console.error("Error saving highlights:", err);
      setError(err.message || "Failed to save highlights");
      toast.error(err.message || "Failed to save highlights");
    } finally {
      setIsSaving(false);
      onClose();
    }
  };

  const handleCancel = () => {
    const safeHighlights = Array.isArray(highlights) ? highlights : [];
    const mappedHighlights = safeHighlights.map((h) => ({
      id: h.id,
      title: h.title || "",
      link: h.url || h.link || "",
      order: h.order || 0,
      is_public: h.is_public !== undefined ? h.is_public : true,
    }));
    setLocalHighlights(mappedHighlights); // Reset to original
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/20 flex items-center justify-center z-50"
      onClick={handleCancel}
    >
      <div
        className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-[#D6DBEA]">
          <h2 className="text-[#3C3C3C] text-base font-bold">HIGHLIGHTS</h2>
          <div className="flex gap-3">
            <button
              onClick={handleCancel}
              disabled={isSaving}
              className="bg-white border border-[#E6E6E6] px-4 py-2 rounded-lg text-[#1E1E1E]"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-[#2283FF] text-[#F5F5F5] px-4 py-2 rounded-lg"
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg">
            {error}
          </div>
        )}

        {/* Highlights List */}
        <div className="mt-6 space-y-4">
          {localHighlights.length > 0 ? (
            localHighlights.map((highlight, index) => (
              <div
                key={highlight.id}
                className="bg-[#F8F9FB] p-4 rounded-lg relative"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="text-[#3C3C3C] text-xs font-bold">
                    HIGHLIGHT {index + 1}
                  </div>
                  <button
                    onClick={() => {
                      const updatedHighlights = localHighlights.filter(
                        (h) => h.id !== highlight.id
                      );
                      setLocalHighlights(updatedHighlights);
                    }}
                    className="text-[#FF2D55] hover:text-[#FF2D55]/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSaving}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 4L4 12M4 4L12 12"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
                <div className="flex gap-4">
                  {/* Video Title - 50% */}
                  <div className="flex-1 flex items-center gap-2">
                    <label className="block w-[25%] text-[#1A212F] text-[13px] font-bold mb-2">
                      Video Title
                    </label>
                    <input
                      type="text"
                      value={highlight.title}
                      onChange={(e) => {
                        const updatedHighlights = localHighlights.map((h) =>
                          h.id === highlight.id
                            ? { ...h, title: e.target.value }
                            : h
                        );
                        setLocalHighlights(updatedHighlights);
                      }}
                      className="w-full bg-white border border-[#D6DBEA] p-2 rounded-lg"
                      placeholder="Add title here"
                    />
                  </div>

                  {/* Link - 50% */}
                  <div className="flex-1 flex items-center gap-2">
                    <label className="block text-[#1A212F] text-[13px] font-bold mb-2">
                      Link
                    </label>
                    <div className="relative w-full">
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <svg
                          width="16"
                          height="9"
                          viewBox="0 0 16 9"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9.99935 1.16699H11.9993C12.4371 1.16699 12.8705 1.25321 13.275 1.42073C13.6794 1.58824 14.0468 1.83377 14.3564 2.1433C14.6659 2.45283 14.9114 2.8203 15.0789 3.22471C15.2465 3.62913 15.3327 4.06259 15.3327 4.50033C15.3327 4.93807 15.2465 5.37152 15.0789 5.77594C14.9114 6.18036 14.6659 6.54782 14.3564 6.85735C14.0468 7.16688 13.6794 7.41241 13.275 7.57992C12.8705 7.74744 12.4371 7.83366 11.9993 7.83366H9.99935M5.99935 7.83366H3.99935C3.56161 7.83366 3.12816 7.74744 2.72374 7.57992C2.31932 7.41241 1.95185 7.16688 1.64233 6.85735C1.01721 6.23223 0.666016 5.38438 0.666016 4.50033C0.666016 3.61627 1.01721 2.76842 1.64233 2.1433C2.26745 1.51818 3.11529 1.16699 3.99935 1.16699H5.99935M5.33268 4.50033H10.666"
                            stroke="#1A212F"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <input
                        type="url"
                        value={highlight.link}
                        onChange={(e) => {
                          const updatedHighlights = localHighlights.map((h) =>
                            h.id === highlight.id
                              ? { ...h, link: e.target.value }
                              : h
                          );
                          setLocalHighlights(updatedHighlights);
                        }}
                        className="w-full bg-white border border-[#D6DBEA] p-2 pl-10 rounded-lg"
                        placeholder="https://"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-[#F8F9FB] p-4 rounded-lg text-[#3C3C3C] text-sm">
              No highlights yet. Use "Add new" to create one.
            </div>
          )}
        </div>

        {/* Add New Button */}
        <button
          onClick={() => {
            const newHighlight = {
              id: -Date.now(),
              title: "",
              link: "",
              order: localHighlights.length,
              is_public: true,
            };
            setLocalHighlights([...localHighlights, newHighlight]);
          }}
          className="mt-4 bg-white border border-[#E6E6E6] px-4 py-2 rounded-lg text-[#1E1E1E]"
          disabled={isSaving}
        >
          Add new
        </button>
      </div>
    </div>
  );
};

export default HighlightsModal;
